import { useEffect, useRef, useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useDebounce } from "./useDebounce";
import type { GeocodingResult } from "@/lib/geocoding";

const ZIP_PATTERN = /^\d{5}$/;

/**
 * Turns a zip code the reader is typing into coordinates.
 *
 * Shared by the sidebar filter and the nearby tab's own prompt, so both spell
 * the same zip the same way and only one of them has to get the ordering
 * right: geocode requests are answered out of order often enough that a slow
 * lookup for a half-typed zip would otherwise land after, and overwrite, the
 * one the reader actually finished typing. Only the newest request is allowed
 * to report a result.
 *
 * The cache is read first, as a query. A zip anyone has searched before is
 * already known, so the overwhelmingly common case resolves from our own
 * database — instantly, reactively, and without a Google call. The action,
 * which is what actually reaches Google, only runs on a genuine first sighting.
 */
export function useZipGeocode(
  zipCode: string,
  onLocationChange: (location: GeocodingResult | null) => void
): { isGeocoding: boolean; error: string | null } {
  const geocodeZipCode = useAction(api.geocoding.geocodeZipCode);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedZipCode = useDebounce(zipCode, 500);
  const latestRequest = useRef(0);

  const isComplete = ZIP_PATTERN.test(debouncedZipCode);
  const cached = useQuery(
    api.geocoding.getCachedZipLocation,
    isComplete ? { zipCode: debouncedZipCode } : "skip"
  );

  // Typed something that is not a zip: say so without asking anyone.
  const isMalformed = debouncedZipCode.length >= 5 && !isComplete;

  useEffect(() => {
    const requestId = ++latestRequest.current;
    const isCurrent = () => latestRequest.current === requestId;

    if (!isComplete) {
      setIsLookingUp(false);
      setError(isMalformed ? "Enter a valid 5-digit zip code" : null);
      onLocationChange(null);
      return;
    }

    // Still reading the cache — say nothing yet rather than flashing an error.
    if (cached === undefined) return;

    if (cached) {
      setIsLookingUp(false);
      setError(null);
      onLocationChange(cached);
      return;
    }

    // Not cached: this is the one path that costs a Google call.
    setIsLookingUp(true);
    setError(null);

    geocodeZipCode({ zipCode: debouncedZipCode })
      .then((result) => {
        if (!isCurrent()) return;
        if (result) {
          onLocationChange(result);
          setError(null);
        } else {
          setError("Couldn't find that zip code");
          onLocationChange(null);
        }
      })
      .catch(() => {
        if (!isCurrent()) return;
        setError("Location lookup failed — try again");
        onLocationChange(null);
      })
      .finally(() => {
        if (isCurrent()) setIsLookingUp(false);
      });
  }, [debouncedZipCode, isComplete, isMalformed, cached, onLocationChange, geocodeZipCode]);

  return {
    isGeocoding: isLookingUp || (isComplete && cached === undefined),
    error,
  };
}
